// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useCallback } from 'react';

/**
 * Hook for boolean state with stable toggle/set functions.
 * Returns [value, toggle, setFalse, setTrue] with memoized callbacks.
 *
 * @example
 * const [isOpen, toggleOpen, close, open] = useBooleanState(false);
 * <button onClick={toggleOpen}>Toggle</button>
 * <button onClick={close}>Close</button>
 */
export function useBooleanState(
  initialValue: boolean
): [boolean, () => void, () => void, () => void] {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => setValue((v) => !v), []);
  const setFalse = useCallback(() => setValue(false), []);
  const setTrue = useCallback(() => setValue(true), []);

  return [value, toggle, setFalse, setTrue];
}
